import type { Middleware } from '@reduxjs/toolkit';
import { configureStore } from '@reduxjs/toolkit';

import { listener } from './middlewares/listener';
import { rtkQueryErrorLogger } from './rtkQueryErrorLogger';
import { accountManagementAPI } from './services/accountManagementAPI';
import { cascadingAPI } from './services/cascadingAPI';
import { checkoutAPI } from './services/checkoutAPI';
import { contentPageAPI } from './services/contentPageAPI';
import { depositAPI } from './services/depositAPI';
import { displayFormAPI } from './services/displayForm';
import { otpAPI } from './services/otpAPI';
import { page3dsAPI } from './services/page3dsAPI';
import { routingAPI } from './services/routingAPI';
import { sessionStateAPI } from './services/sessionStateAPI';
import { withdrawalAPI } from './services/withdrawalAPI';
import { withdrawalRejectAPI } from './services/withdrawalRejectAPI';
import accountManagement from './slices/accountManagement';
import autoFlow from './slices/autoFlow';
import bonus from './slices/bonus';
import carousel from './slices/carousel';
import checkout from './slices/checkout';
import contentPage from './slices/contentPage';
import countryCodeState from './slices/countryCodeState';
import deposit from './slices/deposit';
import errorPopUp from './slices/errorPopUp';
import common from './slices/global';
import kycVerification from './slices/kycVerification';
import methodForm from './slices/methodForm';
import methodGrouping from './slices/methodGrouping';
import notifications from './slices/notifications';
import otp from './slices/otp';
import paySessionState from './slices/paySessionState';
import redirect from './slices/redirect';
import routing from './slices/routing';
import submitForm from './slices/submitForm';
import tabs from './slices/tabs';
import card from './slices/userCard';
import wallet from './slices/wallet';
import withdrawal from './slices/withdrawal';
import withdrawalReject from './slices/withdrawalReject';

const middleware: Middleware[] = [
  listener.middleware,
  accountManagementAPI.middleware,
  depositAPI.middleware,
  checkoutAPI.middleware,
  contentPageAPI.middleware,
  withdrawalAPI.middleware,
  withdrawalRejectAPI.middleware,
  otpAPI.middleware,
  routingAPI.middleware,
  page3dsAPI.middleware,
  displayFormAPI.middleware,
  sessionStateAPI.middleware,
  cascadingAPI.middleware,
  rtkQueryErrorLogger,
];

export const store = configureStore({
  reducer: {
    card,
    common,
    deposit,
    checkout,
    bonus,
    notifications,
    errorPopUp,
    withdrawal,
    withdrawalReject,
    accountManagement,
    contentPage,
    otp,
    routing,
    paySessionState,
    tabs,
    redirect,
    wallet,
    carousel,
    submitForm,
    countryCodeState,
    methodGrouping,
    autoFlow,
    methodForm,
    kycVerification,
    [accountManagementAPI.reducerPath]: accountManagementAPI.reducer,
    [withdrawalAPI.reducerPath]: withdrawalAPI.reducer,
    [depositAPI.reducerPath]: depositAPI.reducer,
    [checkoutAPI.reducerPath]: checkoutAPI.reducer,
    [contentPageAPI.reducerPath]: contentPageAPI.reducer,
    [withdrawalRejectAPI.reducerPath]: withdrawalRejectAPI.reducer,
    [otpAPI.reducerPath]: otpAPI.reducer,
    [sessionStateAPI.reducerPath]: sessionStateAPI.reducer,
    [routingAPI.reducerPath]: routingAPI.reducer,
    [page3dsAPI.reducerPath]: page3dsAPI.reducer,
    [displayFormAPI.reducerPath]: displayFormAPI.reducer,
    [cascadingAPI.reducerPath]: cascadingAPI.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // https://stackoverflow.com/questions/61704805/getting-an-error-a-non-serializable-value-was-detected-in-the-state-when-using
      serializableCheck: false,
    }).concat(middleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
